import { matchRoutes, Navigate, renderMatches, useLocation } from 'react-router-dom'
import { orderedRoutes, routes } from './routes'
import ReactModal from 'react-modal'
import TopBar from '../Sidebar/TopBar'
import Notifications from '../Notifications'
import { useContext, useEffect, useMemo, useRef } from 'react'
import Sidebar from '../Sidebar/Sidebar'
import { userContext } from '../../context/UserContext'
import { ErrorBoundary } from 'react-error-boundary'
import SmallScreenNotice from '../SmallScreenNotice/SmallScreenNotice'
import ErrorPage, { ErrorBoundaryFallback, logError } from '../BaseComponents/ErrorPage/ErrorPage'
import ConnectionStatusBanner from '../ConnectionStatusBanner/ConnectionStatusBanner'
import ConnectionGuard from '../ConnectionGuard/ConnectionGuard'
import useConnectionStatus from '../../hooks/useConnectionStatus'
import useReconnectProbe from '../../hooks/useReconnectProbe'

/**
 * Focus sentinel placed immediately before the main content region.
 * When the user Shift+Tabs from the page, focus lands here and is
 * redirected to the currently active sidebar link so reverse navigation
 * returns to the menu item they activated instead of cycling through
 * the bottom of the sidebar.
 */
const FocusSentinel = () => {
	const sentinelRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleFocus = () => {
			const activeSidebarLink = document.querySelector(
				'nav[aria-label="Sidebar"] a[aria-current="page"]'
			) as HTMLElement | null
			activeSidebarLink?.focus()
		}

		const sentinel = sentinelRef.current
		sentinel?.addEventListener('focus', handleFocus)
		return () => sentinel?.removeEventListener('focus', handleFocus)
	}, [])

	return (
		<div
			ref={sentinelRef}
			tabIndex={0}
			className='sr-only outline-none'
			aria-label='Return to sidebar navigation'
			data-testid='focus-sentinel'
		/>
	)
}

const Router = () => {

	// Root element does not exist in testing environment :-(
	if (process.env.NODE_ENV !== 'test') {
		ReactModal.setAppElement('#root')
	}

	const mainRef = useRef<HTMLDivElement>(null)
	const { checkAccess, userData } = useContext(userContext);
	const isConnected = useConnectionStatus()
	// Security review correction (M4): a logged-out page (e.g. the login form) has no
	// session-scoped data worth protecting behind an inert, unscrollable overlay - and trapping a
	// logged-out visitor there with no escape but a reload is a worse outcome than just showing
	// the banner. The blocking treatment below only applies once there is actually a signed-in
	// user whose stale data it exists to protect.
	const shouldBlockForConnection = !isConnected && Boolean(userData)
	// Issue #759 (final review F7): mounted once here (the single app-shell instance), not inside
	// useConnectionStatus itself, so there is exactly one active probe regardless of how many
	// components (Router, ConnectionStatusBanner) separately call useConnectionStatus() - see
	// useReconnectProbe.ts for why the probe is bounded rather than indefinite.
	useReconnectProbe(isConnected)

	const location = useLocation();
	const validroutes = orderedRoutes.filter(route => {

		const hasAccess = !route.accessRequired || route.accessRequired.every(key => checkAccess(key));
		const hasRole = !route.rolesAllowed || route.rolesAllowed.some(role => role === userData?.role);
		if(route.name === 'Landing Page') {
			console.log({hasRole, hasAccess})
		}
		return hasRole && hasAccess;
	})
	const matches = useMemo(() => {
		const routes = validroutes.map(route => {
			return {
				...route,
				path: route.path,
				element: <ErrorBoundary FallbackComponent={ErrorBoundaryFallback} onError={logError}>{ route.component() }</ErrorBoundary>,
			}
		})
		return matchRoutes(routes, location);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location])

	const routeGroupDefaultPath: Record<string, string> = {
		admin: routes.adminDashboard.path,
		company: routes.companyDashboard.path,
	}

	const roleBasedRedirectPath = userData
		? routeGroupDefaultPath[userData.role] ?? routes.publicLogin.path
		: routes.publicLogin.path;

	const route = matches && matches.length > 0 ? matches[0].route : null
	// A known path the user may not see redirects; a path no route defines is a real 404.
	const isUnknownPath = !route && !matchRoutes(orderedRoutes, location)
	const rendered = route ? renderMatches(matches) : null
	console.log('Shown route', route);

	useEffect(() => {
		if (route) {
			// preventScroll: on iOS Safari, focusing the pane otherwise scrolls the window and hides the top bar.
			mainRef.current?.focus({ preventScroll: true })
			// The pane is the same element across routes, so its scroll offset would otherwise carry over.
			mainRef.current?.scrollTo?.(0, 0)
		}
	}, [location.pathname, route])

	const darkSurface = isUnknownPath
		? !userData
		: route?.darkSurface === 'always' || (route?.darkSurface === 'loggedOut' && !userData)

	if (!route && !isUnknownPath) {
		console.log(`Valid route not found for ${location.pathname}  navigating to `, roleBasedRedirectPath)
		return <Navigate to={roleBasedRedirectPath}/>
	}

	return (
		<div
			className={`flex flex-col h-dvh ${darkSurface ? 'bg-charcoal-700 surface-dark' : 'bg-charcoal-500'}`}
			data-testid='app-shell'
		>
			{/* TopBar and Sidebar deliberately stay interactive while disconnected so logout and navigation remain reachable. */}
			<TopBar />
			{/* Issue #759: global, so every page shows this instead of silently rendering stale data. */}
			<ConnectionStatusBanner />
			<div className="flex content-stretch grow overflow-hidden">
				{ route?.sidebar && <Sidebar validRoutes={validroutes} /> }
				{ route?.sidebar && <FocusSentinel /> }
				{/* tabIndex={-1} keeps the browser from adding this scrollable pane to the tab order with an unstyled default outline */}
				<div
					ref={mainRef}
					// Issue #759 (final review F6): switched to overflow-hidden while disconnected so the
					// pane itself cannot be scrolled (wheel/touch/keyboard) to reveal more stale content
					// from underneath the overlay below - pointer-events-none/inert on route-content alone
					// do not stop the pane's own scroll container from moving.
					className={`grow relative outline-none pb-[env(safe-area-inset-bottom)] ${shouldBlockForConnection ? 'overflow-hidden' : 'overflow-y-auto'}`}
					tabIndex={-1}
					data-testid='main-content'
				>
					{/* Issue #857 (item 4): extracted into ConnectionGuard (components/ConnectionGuard) so this
					    shell and every other signed-in short-circuit shell (OrganisationPickerLayout,
					    VerifyEmailRequired, TrainingNotStarted) share one implementation of the #759
					    aria-hidden/pointer-events-none/inert/opaque-overlay treatment. SmallScreenNotice is
					    inside the same guarded wrapper (security review correction m5) so its dismiss button
					    gets the same treatment instead of staying reachable behind the opaque overlay. */}
					<ConnectionGuard block={shouldBlockForConnection}>
						{ route && !route.mobileReady && <SmallScreenNotice /> }
						{ isUnknownPath ? <ErrorPage variant='notFound' /> : rendered }
					</ConnectionGuard>
					<Notifications />
				</div>
			</div>
		</div>
	)
}

export default Router
